import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { Link } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import SearchIcons from '@/icons/SearchIcons'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { LookupTender, AddTenderFromRemote } from '../../../services'
import type {
    LookupTenderResponse,
    RemoteTenderItem,
    Tender,
} from '../../../types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { formatDateWithFormatter } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'

const TenderSearch = () => {
    const { t } = useTranslation('tenders')
    const { invalidate: invalidateTenders } = useQueryClientCallback({
        pattern: QUERY_KEYS.TENDERS,
        mode: 'startsWith',
    })
    const [code, setCode] = useState('')
    const [result, setResult] = useState<LookupTenderResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [addedMap, setAddedMap] = useState<Map<string, number>>(new Map())
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())

    const handleSearch = async () => {
        const trimmed = code.trim()
        if (!trimmed) return
        setLoading(true)
        setError(null)
        setResult(null)
        try {
            setResult(await LookupTender(trimmed))
        } catch {
            setError(t('search.error'))
        } finally {
            setLoading(false)
        }
    }

    const handleAdd = async (item: RemoteTenderItem) => {
        setLoadingIds((prev) => new Set(prev).add(item.notifyId))
        try {
            const res = await AddTenderFromRemote({
                notifyId: item.notifyId,
                code: item.code,
                name: item.name,
                bidCloseDate: item.bidCloseDate,
                bidForm: item.bidForm,
                field: item.field,
                location: item.location,
                investor: item.investor,
                publishDate: item.publishDate,
                detailUrl: item.detailUrl,
            })
            setAddedMap((prev) =>
                new Map(prev).set(item.notifyId, res.result.id)
            )
            invalidateTenders()
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 409) {
                setAddedMap((prev) =>
                    new Map(prev).set(item.notifyId, err.response?.data?.id)
                )
            } else {
                toast.error(t('search.add_error'))
            }
        } finally {
            setLoadingIds((prev) => {
                const next = new Set(prev)
                next.delete(item.notifyId)
                return next
            })
        }
    }

    const localColumns: DataTableColumn<Tender>[] = [
        {
            key: 'code',
            title: t('code'),
            width: 160,
            cellClassName: 'font-mono text-xs',
        },
        {
            key: 'name',
            title: t('name'),
            width: 320,
        },
        {
            key: 'investor',
            title: t('investor'),
            width: 200,
        },
        {
            key: 'bid_time',
            title: t('bid_time'),
            width: 150,
            render: (_, row) =>
                row.bid_time
                    ? formatDateWithFormatter(row.bid_time, 'HH:mm DD/MM/YYYY')
                    : '-',
        },
        {
            key: 'actions',
            title: '',
            width: 120,
            render: (_, row) => (
                <Link
                    to={generatePathDatacentral(paths.tenderDetail, {
                        tenderId: String(row.id),
                    })}
                    className="text-primary text-xs hover:underline whitespace-nowrap"
                >
                    {t('view_details')} →
                </Link>
            ),
        },
    ]

    const remoteColumns: DataTableColumn<RemoteTenderItem>[] = [
        {
            key: 'code',
            title: t('code'),
            width: 160,
            cellClassName: 'font-mono text-xs',
        },
        {
            key: 'name',
            title: t('name'),
            width: 320,
            render: (_, row) => (
                <div>
                    <span className="line-clamp-2 leading-snug">
                        {row.name || '-'}
                    </span>
                    {row.location && (
                        <span className="block text-xs text-muted-foreground mt-0.5">
                            {row.location}
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'investor',
            title: t('investor'),
            width: 200,
            render: (_, row) => (
                <span className="line-clamp-2 text-xs">
                    {row.investor || '-'}
                </span>
            ),
        },
        {
            key: 'bidCloseDate',
            title: t('bid_time'),
            width: 150,
            render: (_, row) =>
                row.bidCloseDate
                    ? formatDateWithFormatter(
                          row.bidCloseDate,
                          'HH:mm DD/MM/YYYY'
                      )
                    : '-',
        },
        {
            key: 'actions',
            title: '',
            width: 120,
            render: (_, row) => {
                const tenderId = addedMap.get(row.notifyId)
                const isLoading = loadingIds.has(row.notifyId)
                if (tenderId) {
                    return (
                        <Link
                            to={generatePathDatacentral(paths.tenderDetail, {
                                tenderId: String(tenderId),
                            })}
                            className="text-primary text-xs hover:underline whitespace-nowrap"
                        >
                            {t('search.add_success')} →
                        </Link>
                    )
                }
                return (
                    <Button
                        variant="outlined"
                        onClick={() => handleAdd(row)}
                        disabled={isLoading}
                    >
                        {isLoading
                            ? t('search.add_loading')
                            : t('search.add_button')}
                    </Button>
                )
            },
        },
    ]

    return (
        <Paper className="mt-2 p-4 flex flex-col gap-4">
            {/* Info banner */}
            <div className="flex gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <span className="mt-0.5 shrink-0">ℹ️</span>
                <div>
                    <p className="font-medium">{t('search.info_title')}</p>
                    <p className="mt-0.5 text-blue-700">
                        {t('search.info_desc')}
                    </p>
                </div>
            </div>

            {/* Search bar */}
            <div className="flex gap-2 max-w-xl">
                <div className="relative flex-1">
                    <SearchIcons className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                    <input
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder={t('search.placeholder')}
                        className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                </div>
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading || !code.trim()}
                >
                    {loading ? t('search.searching') : t('search.button')}
                </Button>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            {/* Local result */}
            {result?.source === 'local' && (
                <div className="flex flex-col gap-1">
                    <p className="text-sm text-muted-foreground">
                        {t('search.found_local')}
                    </p>
                    <DataTableCustomized
                        columns={localColumns}
                        rows={[result.data]}
                        rowKey={(row) => String(row.id)}
                        size="small"
                        stickyHeader={{ enabled: false }}
                    />
                </div>
            )}

            {/* Remote results */}
            {result?.source === 'remote' && (
                <div className="flex flex-col gap-1">
                    <p className="text-sm text-muted-foreground">
                        {result.data.length === 0
                            ? t('search.no_result')
                            : t('search.found_remote', {
                                  count: result.data.length,
                              })}
                    </p>
                    {result.data.length > 0 && (
                        <DataTableCustomized
                            columns={remoteColumns}
                            rows={result.data}
                            rowKey={(row) => row.id}
                            size="small"
                            stickyHeader={{ enabled: false }}
                        />
                    )}
                </div>
            )}

            {/* Hint state */}
            {!result && !loading && !error && (
                <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
                    <div className="size-12 rounded-full bg-muted flex items-center justify-center mb-1">
                        <SearchIcons className="size-6" />
                    </div>
                    <p className="text-sm">{t('search.hint')}</p>
                </div>
            )}
        </Paper>
    )
}

export default TenderSearch
